import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Inbox, LogOut, ArrowLeft } from "lucide-react";
import Logo from "../components/Logo";
import PaymentsPanel from "../components/PaymentsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const LoginView = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/login`,
        { email, password },
        { withCredentials: true }
      );
      sessionStorage.setItem("gt_admin_token", data.token);
      onLogin();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="w-full max-w-sm glass rounded-3xl p-8 sm:p-10">
        <div className="flex justify-center">
          <Logo />
        </div>
        <h1 className="mt-8 text-center font-display font-bold text-2xl text-white" data-testid="admin-login-title">
          Console admin
        </h1>
        <p className="mt-2 text-center text-sm text-mist">Demandes de démo</p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <input
            data-testid="admin-login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full rounded-xl border border-ink-line bg-ink-soft/80 px-4 py-3 text-sm text-white placeholder:text-mist/50 outline-none focus:border-viol/60 focus:ring-2 focus:ring-viol/25"
          />
          <input
            data-testid="admin-login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe"
            className="w-full rounded-xl border border-ink-line bg-ink-soft/80 px-4 py-3 text-sm text-white placeholder:text-mist/50 outline-none focus:border-viol/60 focus:ring-2 focus:ring-viol/25"
          />
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-iris-deep to-viol px-6 py-3.5 text-sm font-medium text-white transition-all duration-300 hover:shadow-[0_0_40px_rgba(139,92,246,0.5)] disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Se connecter
          </button>
        </form>
        <div className="mt-6 flex items-center gap-3">
          <span className="h-px flex-1 bg-ink-line" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-mist/60">ou</span>
          <span className="h-px flex-1 bg-ink-line" />
        </div>
        {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
        <button
          data-testid="admin-google-login"
          type="button"
          onClick={() => {
            const redirectUrl = window.location.origin + "/admin";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-full border border-ink-line bg-ink-soft/80 px-6 py-3.5 text-sm font-medium text-white transition-all duration-300 hover:border-viol/50 hover:bg-viol/10"
        >
          <GoogleIcon />
          Continuer avec Google
        </button>
      </div>
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const [requests, setRequests] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("demandes");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("gt_admin_token");
      const { data } = await axios.get(`${API}/admin/demo-requests`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      setRequests(data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
        return;
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
    } catch (e) {
      /* ignore */
    }
    sessionStorage.removeItem("gt_admin_token");
    onLogout();
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 glass">
        <div className="mx-auto max-w-5xl px-5 py-4 flex items-center justify-between">
          <Logo compact />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-mist hidden sm:block">
            Demandes de démo
          </span>
          <div className="flex items-center gap-2.5">
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-full border border-ink-line px-4 py-2 text-xs text-mist hover:text-white hover:border-viol/50 transition-all"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Rafraîchir
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-ink-line px-4 py-2 text-xs text-mist hover:text-white hover:border-red-500/50 transition-all"
            >
              <LogOut size={14} />
              Quitter
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-10">
        <div className="mb-8 flex gap-2">
          <button
            data-testid="tab-demandes"
            onClick={() => setTab("demandes")}
            className={`rounded-full px-5 py-2 text-sm transition-all ${tab === "demandes" ? "bg-gradient-to-r from-iris-deep to-viol text-white" : "border border-ink-line text-mist hover:text-white"}`}
          >
            Demandes de démo
          </button>
          <button
            data-testid="tab-paiements"
            onClick={() => setTab("paiements")}
            className={`rounded-full px-5 py-2 text-sm transition-all ${tab === "paiements" ? "bg-gradient-to-r from-iris-deep to-viol text-white" : "border border-ink-line text-mist hover:text-white"}`}
          >
            Acomptes Stripe
          </button>
        </div>
        {tab === "paiements" ? (
          <PaymentsPanel />
        ) : (
        <>
        {loading && requests === null ? (
          <div className="flex items-center justify-center py-24 text-mist">
            <Loader2 size={26} className="animate-spin" />
          </div>
        ) : requests && requests.length === 0 ? (
          <div className="text-center py-24" data-testid="admin-empty-state">
            <Inbox size={40} className="mx-auto text-mist/40" />
            <p className="mt-5 text-mist">Aucune demande pour l'instant.</p>
            <p className="mt-2 text-sm text-mist/60">
              Les demandes envoyées depuis le formulaire apparaîtront ici.
            </p>
          </div>
        ) : (
          <>
            <p className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-viol-soft" data-testid="admin-requests-count">
              {requests?.length || 0} demande{requests?.length > 1 ? "s" : ""}
            </p>
            <div className="space-y-3" data-testid="admin-requests-list">
              {requests?.map((r) => (
                <article
                  key={r.id}
                  data-testid="admin-request-item"
                  className="rounded-2xl border border-ink-line bg-ink-surface/60 p-5 sm:p-6 transition-colors duration-200 hover:border-viol/35"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-display font-bold text-lg text-white">
                        {r.first_name} — {r.company}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-mist">
                        <a href={`mailto:${r.email}`} className="hover:text-viol-pale transition-colors">
                          {r.email}
                        </a>
                        <a href={`tel:${r.phone}`} className="hover:text-viol-pale transition-colors">
                          {r.phone}
                        </a>
                      </div>
                    </div>
                    <div className="text-right">
                      {r.pack && (
                        <span className="inline-block rounded-full border border-viol/35 bg-viol/10 px-3 py-1 font-mono text-[11px] text-viol-pale">
                          {r.pack}
                        </span>
                      )}
                      <p className="mt-2 font-mono text-[11px] text-mist/60">
                        {new Date(r.created_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}
                      </p>
                    </div>
                  </div>
                  {r.ai && (
                    <div className="mt-4 flex flex-wrap gap-2 border-t border-ink-line pt-4" data-testid="admin-request-ai">
                      {r.ai.ai_classified ? (
                        <>
                          <span
                            className={`inline-flex items-center rounded-full border px-3 py-1 font-mono text-[11px] ${
                              r.ai.heat === "chaud"
                                ? "border-rose-400/40 bg-rose-500/10 text-rose-300"
                                : r.ai.heat === "tiède"
                                ? "border-amber-400/40 bg-amber-500/10 text-amber-300"
                                : "border-sky-400/40 bg-sky-500/10 text-sky-300"
                            }`}
                          >
                            {r.ai.heat === "chaud" ? "Chaud" : r.ai.heat === "tiède" ? "Tiède" : "Froid"}
                          </span>
                          <span className="inline-flex items-center rounded-full border border-ink-line px-3 py-1 font-mono text-[11px] text-mist">
                            {{ demo: "Démo", curieux: "Curieux", recrutement: "RH / Partenariat", spam: "Sans valeur" }[r.ai.intent] || r.ai.intent}
                          </span>
                          <span className="inline-flex items-center rounded-full border border-ink-line px-3 py-1 font-mono text-[11px] text-mist">
                            {r.ai.sector}
                          </span>
                          {r.ai.spam && (
                            <span className="inline-flex items-center rounded-full border border-red-400/40 bg-red-500/10 px-3 py-1 font-mono text-[11px] text-red-300">
                              Suspect
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="font-mono text-[11px] text-mist/60">Analyse IA indisponible</span>
                      )}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </>
        )}
        </>
        )}
      </main>
    </div>
  );
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A9 9 0 0 0 9 18Z" />
    <path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.96H.96a9 9 0 0 0 0 8.08l3.01-2.32Z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.42 0 9 0A9 9 0 0 0 .96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58Z" />
  </svg>
);

const AuthCallback = ({ onLogin }) => {
  const hasProcessed = useRef(false);
  const location = useLocation();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    window.history.replaceState({}, "", "/admin");
    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/admin/google-session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        sessionStorage.setItem("gt_admin_token", data.token);
        onLogin();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
        setTimeout(() => window.location.replace("/admin"), 1500);
      }
    })();
  }, [location.hash, onLogin]);

  return (
    <div className="min-h-screen flex items-center justify-center text-mist">
      <div className="text-center">
        <Loader2 size={26} className="animate-spin mx-auto" />
        <p className="mt-4 text-sm">Connexion avec Google en cours…</p>
      </div>
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.hash?.includes("session_id=")) return;
    const check = async () => {
      const token = sessionStorage.getItem("gt_admin_token");
      try {
        await axios.get(`${API}/admin/me`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        });
        setAuthed(true);
      } catch (e) {
        setAuthed(false);
      }
    };
    check();
  }, [location.hash]);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback onLogin={() => setAuthed(true)} />;
  }

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-mist">
        <Loader2 size={26} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink">
      {authed ? (
        <Dashboard onLogout={() => setAuthed(false)} />
      ) : (
        <>
          <div className="pt-8 pl-5 sm:pl-8">
            <a href="/" data-testid="admin-back-home" className="inline-flex items-center gap-2 text-sm text-mist hover:text-white transition-colors">
              <ArrowLeft size={15} />
              Retour au site
            </a>
          </div>
          <LoginView onLogin={() => setAuthed(true)} />
        </>
      )}
    </div>
  );
}
